/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { ReactElement } from 'react';

import type { AiBrand } from './types.js';

export interface StubPanelProps {
  readonly brand: AiBrand;
}

const BRAND_CONFIG: Record<
  AiBrand,
  { readonly title: string; readonly description: string; readonly modifier: string }
> = {
  standard: {
    title: 'Ecsper Standard',
    modifier: 'ecs-ai-panel--standard',
    description:
      'Compliance checking against CPR Part 35 requirements, section completeness analysis, and declaration validity.',
  },
  challenge: {
    title: 'Ecsper Challenge',
    modifier: 'ecs-ai-panel--challenge',
    description: 'Gap detection, unsupported assertions, and incomplete methodology analysis.',
  },
  persona: {
    title: 'Ecsper Persona',
    modifier: 'ecs-ai-panel--persona',
    description: 'Tone and register guidance, readability scoring, and impartiality checks.',
  },
};

function StandardBrandIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M12 3l8 4v6c0 5-3.5 8.5-8 9.5C7.5 21.5 4 18 4 13V7l8-4z" />
    </svg>
  );
}

function ChallengeBrandIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path strokeLinecap="round" d="M20 20l-3-3" />
    </svg>
  );
}

function PersonaBrandIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="4" />
      <path strokeLinecap="round" d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  );
}

function BrandIcon({ brand: brandKey }: { readonly brand: AiBrand }): ReactElement {
  switch (brandKey) {
    case 'standard':
      return <StandardBrandIcon />;
    case 'challenge':
      return <ChallengeBrandIcon />;
    case 'persona':
      return <PersonaBrandIcon />;
  }
}

/**
 * Shared stub surface for AI brand panels until the AI Gateway activates at Stage 6.
 */
export function StubPanel({ brand: brandKey }: StubPanelProps): ReactElement {
  const config =
    brandKey === 'standard'
      ? BRAND_CONFIG.standard
      : brandKey === 'challenge'
        ? BRAND_CONFIG.challenge
        : BRAND_CONFIG.persona;

  return (
    <details className={`ecs-ai-panel ecs-ai-panel--stub ${config.modifier}`}>
      <summary className="ecs-ai-panel__header">
        <span className="ecs-ai-panel__header-icon" aria-hidden="true">
          <BrandIcon brand={brandKey} />
        </span>
        <span className="ecs-ai-panel__header-title">{config.title}</span>
      </summary>
      <div className="ecs-ai-panel__body">
        <p className="ecs-ai-panel__description">{config.description}</p>
        <span className="ecs-badge ecs-ai-panel__tier-badge">Available with Ecsper AI</span>
      </div>
    </details>
  );
}
